import {
  BANK_ACCCOUNT_MODAL,
  PAYMENT_METHOD_MODAL_NAMESPACE,
  RECENT_PAYMENTS_NAMESPACE,
} from "constants/i18n-namespaces";
import { Accordion, AccordionGroup } from "kubra-ux-forge";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { IPaymentRecord } from "types/Api/Payment/paymentRecord";
import { mappingPaymentStatus } from "utilities/helper";
import { getLastTransactionDate } from "./paymentTransactionHelpers";

interface PaymentAccordionsProps {
  upcomingPayment?: IPaymentRecord;
  recentPayments: IPaymentRecord[];
  createPaymentIntentContent: (
    payment: IPaymentRecord,
    isExpanded: boolean,
  ) => React.ReactNode;
}

const StyledAccordionWrapper = styled.div`
  width: 100%;
  > div > div > div > div label {
    font-size: 1.8rem;
  }
`;

export const PaymentAccordions = ({
  upcomingPayment,
  recentPayments,
  createPaymentIntentContent,
}: PaymentAccordionsProps) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const handleAccordionClick = (id?: string) => {
    if (!id) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.clear();
        next.add(id);
      }
      return next;
    });
  };

  const sortPaymentsByDate = (payments: IPaymentRecord[]): IPaymentRecord[] => {
    return [...payments].sort((a, b) => {
      const dateA = new Date(a.created).getTime();
      const dateB = new Date(b.created).getTime();
      return dateA - dateB;
    });
  };

  const sortedPayments = sortPaymentsByDate(recentPayments);

  const { t, i18n } = useTranslation([
    RECENT_PAYMENTS_NAMESPACE,
    PAYMENT_METHOD_MODAL_NAMESPACE,
    BANK_ACCCOUNT_MODAL,
  ]);

  const getAccordionHeader = (status: string): string | undefined => {
    const mappedStatus = mappingPaymentStatus(status);
    switch (mappedStatus) {
      case "paid":
        return t("payment-title-approved");
      case "refunded":
      case "scheduled refund":
      case "pending refund":
      case "refund cancelled":
      case "refund failed":
        return t("payment-title-refund");
      case "pending":
        return t("payment-title-pending");
      case "cancelled":
        return t("payment-title-cancelled");
      case "failed":
        return t("payment-title-failed");
      case "returned":
      case "return cancelled":
        return t("payment-title-returned");
      default:
        return undefined;
    }
  };

  if (!upcomingPayment) {
    return (
      <AccordionGroup>
        {sortedPayments.map((payment) => (
          <StyledAccordionWrapper key={i18n.language + payment.id}>
            <Accordion
              id={payment.id}
              header={`${getAccordionHeader(
                payment.latestItemTransactionStatus,
              )} ${moment
                .utc(getLastTransactionDate(payment))
                .format("MM/DD/YY")}`}
              content={createPaymentIntentContent(
                payment,
                expandedIds.has(payment.id),
              )}
              key={payment.id}
              iconPosition="left"
              data-testid="recentPaymentAccordion"
              onClick={handleAccordionClick}
            />
          </StyledAccordionWrapper>
        ))}
      </AccordionGroup>
    );
  }

  return (
    <AccordionGroup
      key={i18n.language + upcomingPayment.id + recentPayments.length}
    >
      <StyledAccordionWrapper key={i18n.language + upcomingPayment.id}>
        <Accordion
          id={upcomingPayment.id}
          key={upcomingPayment.id}
          iconPosition="left"
          header={`${t("payment-title-scheduled")} ${moment
            .utc(upcomingPayment.paymentDate)
            .format("MM/DD/YY")}`}
          content={createPaymentIntentContent(
            upcomingPayment,
            expandedIds.has(upcomingPayment.id),
          )}
          onClick={handleAccordionClick}
        />
      </StyledAccordionWrapper>
      <>
        {sortedPayments.map((payment) => (
          <StyledAccordionWrapper key={i18n.language + payment.id}>
            <Accordion
              id={payment.id}
              header={`${getAccordionHeader(
                payment.latestItemTransactionStatus,
              )} ${moment.utc(getLastTransactionDate(payment)).format("MM/DD/YY")}`}
              content={createPaymentIntentContent(
                payment,
                expandedIds.has(payment.id),
              )}
              key={payment.id}
              iconPosition="left"
              data-testid="recentPaymentAccordion"
              onClick={handleAccordionClick}
            />
          </StyledAccordionWrapper>
        ))}
      </>
    </AccordionGroup>
  );
};
